import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import CatalogueProductCard from '../components/directory/CatalogueProductCard';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';
const SUPPLIERS_LIMIT = 200;
const RESULTS_PER_PAGE = 48;

const FONT = "'Raleway', 'SF Pro Display', sans-serif";

function getInitials(name) {
  return String(name || '')
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();
}

// ── Supplier card ────────────────────────────────────────────────────────────
// Two shapes from one component: a supplier with footprints leads with its
// coverage, a supplier without leads with the action that changes that. An
// empty progress bar is never drawn — sixteen of them stacked down a list is
// the whole reason this page exists.
function SupplierCard({ supplier, onBrowse, onRequest }) {
  const total = supplier.product_count || 0;
  const assessed = Math.min(supplier.completed_assessment_count || 0, total);
  const hasFootprints = assessed > 0;
  const coverage = total > 0 ? Math.max(2, Math.round((assessed / total) * 100)) : 0;

  return (
    <div
      onClick={onBrowse}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: 16,
        border: '1.5px solid #e8f0eb',
        borderRadius: 8,
        background: '#ffffff',
        cursor: 'pointer',
        transition: 'border-color 0.12s, box-shadow 0.12s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = '#06a457';
        e.currentTarget.style.boxShadow = '0 2px 12px rgba(6,164,87,0.10)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = '#e8f0eb';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
        <div style={{
          width: 40,
          height: 40,
          borderRadius: 999,
          background: supplier.logo_url ? '#ffffff' : '#1a2e20',
          border: supplier.logo_url ? '1px solid #e8f0eb' : 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#ffffff',
          fontWeight: 600,
          fontSize: 13,
          flexShrink: 0,
          overflow: 'hidden',
        }}>
          {supplier.logo_url
            ? <img src={supplier.logo_url} alt={supplier.name} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 5 }} />
            : getInitials(supplier.name)}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            fontSize: 15,
            fontWeight: 600,
            color: '#052b16',
            lineHeight: 1.25,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {supplier.name}
          </div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
            {total.toLocaleString()} product{total === 1 ? '' : 's'}
            {supplier.country ? ` · ${supplier.country}` : ''}
          </div>
        </div>
      </div>

      {hasFootprints ? (
        <div>
          <div style={{ fontSize: 12.5, color: '#4a5568', marginBottom: 5 }}>
            <strong style={{ color: '#06a457', fontWeight: 700 }}>{assessed.toLocaleString()}</strong>
            {' products with a verified footprint'}
          </div>
          <div style={{ height: 4, borderRadius: 2, background: '#e8f0eb', overflow: 'hidden' }}>
            <div style={{ width: `${coverage}%`, height: '100%', background: '#06a457', borderRadius: 2 }} />
          </div>
        </div>
      ) : (
        <div style={{ fontSize: 12.5, color: '#6b7280', lineHeight: 1.45 }}>
          Browsable catalogue, no footprints yet — ask them for the products you sell.
        </div>
      )}

      <div style={{ display: 'flex', gap: 7, marginTop: 'auto' }}>
        <button
          type="button"
          onClick={e => { e.stopPropagation(); onBrowse(); }}
          style={{
            flex: 1,
            padding: '7px 12px',
            fontSize: 12.5,
            fontWeight: 600,
            fontFamily: FONT,
            letterSpacing: '0.03em',
            // Black is the primary action on a card. On a footprint-ready
            // supplier that's Browse; on a catalogue-only one the black belongs
            // to Request PCFs, so Browse steps back to a green outline.
            color: hasFootprints ? '#ffffff' : '#06a457',
            background: hasFootprints ? '#000000' : '#ffffff',
            border: `1.5px solid ${hasFootprints ? '#000000' : '#06a457'}`,
            borderRadius: 6,
            cursor: 'pointer',
          }}
        >
          Browse
        </button>
        {!hasFootprints && (
          <button
            type="button"
            onClick={e => { e.stopPropagation(); onRequest(); }}
            style={{
              flex: 1,
              padding: '7px 12px',
              fontSize: 12.5,
              fontWeight: 600,
              fontFamily: FONT,
              letterSpacing: '0.03em',
              color: '#ffffff',
              background: '#000000',
              border: '1.5px solid #000000',
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            Request PCFs
          </button>
        )}
      </div>
    </div>
  );
}

function SectionHeading({ label, count, blurb }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <h2 style={{
          margin: 0,
          fontSize: 15,
          fontWeight: 600,
          color: '#052b16',
          letterSpacing: '0.01em',
        }}>
          {label}
        </h2>
        <span style={{ fontSize: 13, color: '#9ca3af' }}>{count}</span>
      </div>
      {blurb && (
        <p style={{ margin: '5px 0 0', fontSize: 13, color: '#6b7280', lineHeight: 1.5, maxWidth: 620 }}>
          {blurb}
        </p>
      )}
    </div>
  );
}

const GRID = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(248px, 1fr))', gap: 14 };

export default function SupplierDirectory() {
  const { supplierInfo, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [suppliers, setSuppliers] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [footprintOnly, setFootprintOnly] = useState(true);
  const [results, setResults] = useState([]);
  const [resultCount, setResultCount] = useState(0);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [pulling, setPulling] = useState(false);
  const [pullMessage, setPullMessage] = useState('');
  const debounceTimer = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (supplierInfo && supplierInfo.account_type !== 'Distributor') {
      navigate('/products', { replace: true });
    }
  }, [authLoading, supplierInfo, navigate]);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/distributor/directory`, {
      params: { page: 1, items_per_page: SUPPLIERS_LIMIT },
    }).then(res => {
      setSuppliers(res.data.data || []);
    }).catch(() => {
      setLoadError('Could not load the supplier directory.');
    }).finally(() => setSuppliersLoading(false));
  }, []);

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setPage(1);
    }, 350);
    return () => debounceTimer.current && clearTimeout(debounceTimer.current);
  }, [searchInput]);

  useEffect(() => {
    if (!searchQuery) {
      setResults([]);
      setResultCount(0);
      return;
    }
    let cancelled = false;
    setResultsLoading(true);
    axios.get(`${API_BASE_URL}/distributor/directory/search`, {
      params: {
        search: searchQuery,
        has_footprint: footprintOnly ? 'true' : '',
        page,
        items_per_page: RESULTS_PER_PAGE,
      },
    }).then(res => {
      if (cancelled) return;
      setResults(res.data.data || []);
      setResultCount(res.data.count || 0);
    }).catch(() => {
      if (!cancelled) { setResults([]); setResultCount(0); }
    }).finally(() => {
      if (!cancelled) setResultsLoading(false);
    });
    return () => { cancelled = true; };
  }, [searchQuery, footprintOnly, page]);

  useEffect(() => { setSelectedIds(new Set()); setPullMessage(''); }, [searchQuery, footprintOnly, page]);

  const withFootprints = useMemo(
    () => suppliers.filter(s => (s.completed_assessment_count || 0) > 0),
    [suppliers]
  );
  const catalogueOnly = useMemo(
    () => suppliers.filter(s => (s.completed_assessment_count || 0) === 0),
    [suppliers]
  );
  const browse = useCallback((id) => navigate(`/suppliers/${id}/catalogue`), [navigate]);
  const requestFrom = useCallback((id) => navigate(`/suppliers/${id}/catalogue?request_pcfs=1`), [navigate]);

  const toggleSelect = (productId, isLinked) => {
    if (isLinked) return;
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId); else next.add(productId);
      return next;
    });
  };

  // Selection can span suppliers here, and the bulk-link endpoint is scoped to
  // one supplier at a time, so the pull is grouped and issued per supplier.
  const pullSelected = async () => {
    if (selectedIds.size === 0) return;
    setPulling(true);
    setPullMessage('');
    const bySupplier = new Map();
    for (const p of results) {
      if (!selectedIds.has(p.id)) continue;
      if (!bySupplier.has(p.supplier_id)) bySupplier.set(p.supplier_id, []);
      bySupplier.get(p.supplier_id).push(p.id);
    }
    try {
      const outcomes = await Promise.all(
        Array.from(bySupplier.entries()).map(([supplierId, productIds]) =>
          axios.post(`${API_BASE_URL}/distributor/links/bulk`, {
            supplier_id: supplierId,
            product_ids: productIds,
          })
            .then(r => ({ supplierId, ok: true, linked: r.data?.linked ?? 0 }))
            .catch(() => ({ supplierId, ok: false, linked: 0 }))
        )
      );
      const linked = outcomes.reduce((n, o) => n + o.linked, 0);
      const failed = outcomes.filter(o => !o.ok).length;
      setPullMessage(
        failed > 0
          ? `Pulled ${linked.toLocaleString()} product${linked === 1 ? '' : 's'}; ${failed} supplier${failed === 1 ? '' : 's'} failed.`
          : `Pulled ${linked.toLocaleString()} product${linked === 1 ? '' : 's'} into your catalogue.`
      );
      // Reflect the new "In your catalogue" state without a full refetch, for
      // the suppliers whose pull actually succeeded.
      const pulledFrom = new Set(outcomes.filter(o => o.ok).map(o => o.supplierId));
      setResults(prev => prev.map(p => (
        selectedIds.has(p.id) && pulledFrom.has(p.supplier_id) ? { ...p, already_linked: true } : p
      )));
      setSelectedIds(new Set());
    } catch {
      setPullMessage('Could not pull those products. Please try again.');
    } finally {
      setPulling(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(resultCount / RESULTS_PER_PAGE));
  const searching = !!searchQuery;

  return (
    <div style={{
      minHeight: 'calc(100vh - 59px)',
      background: '#ffffff',
      fontFamily: FONT,
      padding: '28px 32px 60px',
    }}>
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>

        {/* Header */}
        <div style={{
          fontSize: 10,
          fontWeight: 600,
          color: '#9ca3af',
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          marginBottom: 4,
        }}>
          Directory
        </div>
        <h1 style={{
          margin: 0,
          fontSize: 28,
          fontWeight: 300,
          color: '#052b16',
          letterSpacing: '-0.3px',
          lineHeight: 1.1,
        }}>
          Find products with a carbon footprint
        </h1>
        {/* Search across every catalogue at once — the thing 22 separate
            catalogues can't do. */}
        <div style={{ marginTop: 22, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1 1 360px', maxWidth: 520 }}>
            <svg
              width="15" height="15" viewBox="0 0 24 24" fill="none"
              style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
            >
              <circle cx="11" cy="11" r="7" stroke="#9ca3af" strokeWidth="2" />
              <path d="M16.5 16.5L21 21" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search every catalogue — tote bag, drink bottle, hoodie…"
              style={{
                width: '100%',
                padding: '11px 14px 11px 36px',
                fontSize: 14,
                border: '1.5px solid #d4ede2',
                borderRadius: 7,
                outline: 'none',
                fontFamily: FONT,
                color: '#052b16',
                background: '#ffffff',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ display: 'inline-flex', border: '1.5px solid #d4ede2', borderRadius: 7, overflow: 'hidden' }}>
            {[
              { key: true, label: 'With footprints' },
              { key: false, label: 'All products' },
            ].map(opt => (
              <button
                key={String(opt.key)}
                type="button"
                onClick={() => { setFootprintOnly(opt.key); setPage(1); }}
                style={{
                  padding: '10px 15px',
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: FONT,
                  letterSpacing: '0.02em',
                  border: 'none',
                  cursor: 'pointer',
                  background: footprintOnly === opt.key ? '#06a457' : '#ffffff',
                  color: footprintOnly === opt.key ? '#ffffff' : '#4a5568',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {loadError && (
          <div style={{
            marginTop: 18,
            padding: '10px 14px',
            background: '#fff5f5',
            border: '1px solid #fca5a5',
            borderRadius: 6,
            color: '#dc2626',
            fontSize: 13,
          }}>
            {loadError}
          </div>
        )}

        {/* ── Search results ─────────────────────────────────────────── */}
        {searching ? (
          <div style={{ marginTop: 26 }}>
            <SectionHeading
              label={resultsLoading ? 'Searching…' : `${resultCount.toLocaleString()} result${resultCount === 1 ? '' : 's'} for "${searchQuery}"`}
              count={footprintOnly ? 'with a verified footprint' : 'across all catalogues'}
            />

            {!resultsLoading && results.length === 0 ? (
              <div style={{
                padding: '28px 22px',
                border: '1.5px dashed #d4ede2',
                borderRadius: 8,
                background: '#fafcfb',
                fontSize: 13.5,
                color: '#6b7280',
                lineHeight: 1.6,
              }}>
                {footprintOnly ? (
                  <>
                    No product with a footprint matches “{searchQuery}” yet.{' '}
                    <button
                      type="button"
                      onClick={() => setFootprintOnly(false)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        font: 'inherit',
                        fontWeight: 600,
                        color: '#06a457',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                      }}
                    >
                      Search all products
                    </button>
                    {' '}and request a footprint from the supplier.
                  </>
                ) : (
                  <>Nothing in any catalogue matches “{searchQuery}”.</>
                )}
              </div>
            ) : (
              <>
                <div style={GRID}>
                  {results.map(p => (
                    <CatalogueProductCard
                      key={p.id}
                      product={p}
                      selected={selectedIds.has(p.id)}
                      onToggle={toggleSelect}
                      onRequestPcf={() => navigate(
                        `/suppliers/${p.supplier_id}/catalogue?request=${p.id}`
                        + `&request_name=${encodeURIComponent(p.name || '')}`
                      )}
                      showSupplier
                    />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 24 }}>
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      style={{
                        padding: '6px 14px',
                        border: '1px solid #d4ede2',
                        borderRadius: 4,
                        background: page === 1 ? '#f5faf7' : '#ffffff',
                        color: page === 1 ? '#9ca3af' : '#052b16',
                        cursor: page === 1 ? 'default' : 'pointer',
                        fontSize: 13,
                        fontFamily: FONT,
                      }}
                    >
                      Previous
                    </button>
                    <span style={{ fontSize: 13, color: '#4a5568' }}>Page {page} of {totalPages}</span>
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      style={{
                        padding: '6px 14px',
                        border: '1px solid #d4ede2',
                        borderRadius: 4,
                        background: page === totalPages ? '#f5faf7' : '#ffffff',
                        color: page === totalPages ? '#9ca3af' : '#052b16',
                        cursor: page === totalPages ? 'default' : 'pointer',
                        fontSize: 13,
                        fontFamily: FONT,
                      }}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          /* ── Supplier bands ───────────────────────────────────────── */
          <div style={{ marginTop: 28 }}>
            {suppliersLoading ? (
              <div style={GRID}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} style={{ height: 148, border: '1.5px solid #e8f0eb', borderRadius: 8, background: '#fafcfb' }} />
                ))}
              </div>
            ) : suppliers.length === 0 ? (
              <div style={{ fontSize: 14, color: '#9ca3af' }}>No suppliers available yet.</div>
            ) : (
              <>
                {withFootprints.length > 0 && (
                  <section style={{ marginBottom: 36 }}>
                    <SectionHeading
                      label="Footprint-ready"
                      count={`${withFootprints.length} supplier${withFootprints.length === 1 ? '' : 's'}`}
                      blurb="These suppliers have verified footprints you can pull straight into your catalogue."
                    />
                    <div style={GRID}>
                      {withFootprints.map(s => (
                        <SupplierCard
                          key={s.id}
                          supplier={s}
                          onBrowse={() => browse(s.id)}
                          onRequest={() => requestFrom(s.id)}
                        />
                      ))}
                    </div>
                  </section>
                )}

                {catalogueOnly.length > 0 && (
                  <section>
                    <SectionHeading
                      label="Catalogue only"
                      count={`${catalogueOnly.length} supplier${catalogueOnly.length === 1 ? '' : 's'}`}
                      blurb="Full catalogues you can browse and pull from, with no footprints computed yet. Requesting one tells the supplier which products their distributors actually need — it's the fastest way to get data on the SKUs you sell."
                    />
                    <div style={GRID}>
                      {catalogueOnly.map(s => (
                        <SupplierCard
                          key={s.id}
                          supplier={s}
                          onBrowse={() => browse(s.id)}
                          onRequest={() => requestFrom(s.id)}
                        />
                      ))}
                    </div>
                  </section>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Selection action bar — only while a cross-supplier selection exists */}
      {searching && (selectedIds.size > 0 || pullMessage) && (
        <div style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          padding: '12px 32px',
          background: '#ffffff',
          borderTop: '1px solid #e8f0eb',
          boxShadow: '0 -4px 18px rgba(5,43,22,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 14,
          zIndex: 900,
        }}>
          <span style={{ fontSize: 13, color: pullMessage ? '#065f46' : '#4a5568' }}>
            {pullMessage || `${selectedIds.size} product${selectedIds.size === 1 ? '' : 's'} selected across suppliers`}
          </span>
          {selectedIds.size > 0 && (
            <button
              type="button"
              onClick={pullSelected}
              disabled={pulling}
              style={{
                background: '#06a457',
                color: '#ffffff',
                border: 'none',
                borderRadius: 6,
                padding: '9px 18px',
                fontWeight: 600,
                fontSize: 13,
                cursor: pulling ? 'default' : 'pointer',
                fontFamily: FONT,
                letterSpacing: '0.03em',
              }}
            >
              {pulling ? 'Pulling…' : `Pull ${selectedIds.size} into my catalogue →`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
